import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { userServices } from '../../../services'
import { showBanner } from '../../../global-state/banner-slice'
import { updateConfiguration } from '../../../global-state/investor-slice'
import { basicDetailsSchema } from '../../../lib/utils/schema'
import { CBLogoV2 } from '../../../lib/components/Icon'
import { captureEvent } from '../../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../../lib/utils/constants'
import {
  BasicDetailsFormValues,
  calculateAge,
  combineFullName,
  formatDateOfBirthForApi,
  getOnboardingFlagsFromProfileData,
  mapApiProfileToBasicDetails,
  POST_ONBOARDING_PATH,
  resolveOnboardingPath,
  resolveOnboardingPathFromFlags,
  splitFullName,
} from '../../../lib/utils/onboarding'
import {
  getPostLoginNavigation,
  resolvePostOnboardingDestination,
} from '../../../lib/utils/post-login'
import { BasicDetailsPresenter } from './basic-details-presenter'
import './basic-details.scss'

const emptyValues = (): BasicDetailsFormValues => ({
  firstName: '',
  lastName: '',
  dateOfBirth: null,
  countryOfResidence: '',
  taxResidency: '',
  citizenship: '',
  callingPhone: '',
  clientCv: '',
})

const BasicDetailsContainer = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { authToken, email, name, userId, acceptedNDA } = useSelector(
    (state: RootState) => state.investor,
  )
  const [initialValues, setInitialValues] = useState<BasicDetailsFormValues>(emptyValues)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const profileRequestRef = useRef(0)

  const syncOnboardingFlags = useCallback(
    (profile: ReturnType<typeof mapApiProfileToBasicDetails>) => {
      const flags = getOnboardingFlagsFromProfileData(profile)
      dispatch(
        updateConfiguration({ name: 'basicDetailsCompleted', value: flags.basicDetailsCompleted }),
      )
      dispatch(
        updateConfiguration({ name: 'riskProfileCompleted', value: flags.riskProfileCompleted }),
      )
      dispatch(
        updateConfiguration({ name: 'riskProfilerRequired', value: flags.riskProfilerRequired }),
      )
    },
    [dispatch],
  )

  const resolveDestinationFromOnboardingPath = useCallback(
    (path: ReturnType<typeof resolveOnboardingPath>) => {
      return path === POST_ONBOARDING_PATH ? resolvePostOnboardingDestination(acceptedNDA) : path
    },
    [acceptedNDA],
  )

  const redirectIfComplete = useCallback(
    (profile: ReturnType<typeof mapApiProfileToBasicDetails>) => {
      const path = resolveOnboardingPath(profile)
      if (path !== '/onboarding/basic-details') {
        navigate(resolveDestinationFromOnboardingPath(path), { replace: true })
      }
    },
    [navigate, resolveDestinationFromOnboardingPath],
  )

  useEffect(() => {
    if (!authToken) {
      navigate('/login', { replace: true })
      return
    }

    const requestId = ++profileRequestRef.current

    const loadProfile = async () => {
      setIsLoading(true)
      try {
        const response = await userServices.getUserProfile_v1({
          Authorization: authToken,
        })

        if (requestId !== profileRequestRef.current) return

        if (response?.status === 200 && response.data) {
          const profile = mapApiProfileToBasicDetails(
            { ...response.data, name: response.data.name || name },
            email,
          )
          setInitialValues({
            firstName: profile.firstName,
            lastName: profile.lastName,
            dateOfBirth: profile.dateOfBirth,
            countryOfResidence: profile.countryOfResidence,
            taxResidency: profile.taxResidency,
            citizenship: profile.citizenship,
            callingPhone: profile.callingPhone,
            clientCv: profile.clientCv,
          })
          syncOnboardingFlags(profile)
          redirectIfComplete(profile)
        } else {
          setInitialValues(emptyValues())
          if (name) {
            const { firstName, lastName } = splitFullName(name)
            setInitialValues((prev) => ({ ...prev, firstName, lastName }))
          }
        }
      } finally {
        if (requestId === profileRequestRef.current) {
          setIsLoading(false)
        }
      }
    }

    loadProfile()
  }, [authToken, email, navigate, redirectIfComplete, syncOnboardingFlags])

  const handleSubmit = async (values: BasicDetailsFormValues) => {
    if (!authToken) return

    setIsSubmitting(true)
    const fullName = combineFullName(values.firstName, values.lastName)
    const age = calculateAge(values.dateOfBirth)

    const response = await userServices.userProfileUpdate_v1(
      {
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: formatDateOfBirthForApi(values.dateOfBirth),
        age: age ?? undefined,
        countryOfResidence: values.countryOfResidence,
        taxResidency: values.taxResidency,
        citizenship: values.citizenship,
        phoneNumber: values.callingPhone,
        communicationPhone: values.callingPhone,
        occupation: values.clientCv.trim(),
        basicDetailsCompleted: true,
      },
      { Authorization: authToken },
    )
    setIsSubmitting(false)

    if (response?.status === 200) {
      profileRequestRef.current += 1
      dispatch(updateConfiguration({ name: 'basicDetailsCompleted', value: true }))
      dispatch(updateConfiguration({ name: 'phone', value: values.callingPhone }))
      dispatch(updateConfiguration({ name: 'name', value: fullName }))
      captureEvent(EVENTS.FILLED_INVESTOR_DETAIL, { userId })
      const session = await getPostLoginNavigation(authToken, userId, email, name)
      dispatch(
        updateConfiguration({
          name: 'riskProfileCompleted',
          value: session.riskProfileCompleted,
        }),
      )
      dispatch(
        updateConfiguration({
          name: 'riskProfilerRequired',
          value: session.riskProfilerRequired,
        }),
      )
      dispatch(
        showBanner({
          text: 'Your details have been saved. You can now explore opportunities.',
          variant: 'success-banner',
        }),
      )
      navigate(resolveOnboardingPathFromFlags(true, session.riskProfilerRequired), {
        replace: true,
      })
      return
    }

    dispatch(
      showBanner({
        text: response?.data?.message || 'Something went wrong. Please try again.',
        variant: 'error-banner',
      }),
    )
  }

  if (!authToken) return null

  return (
    <div className='basic-details-page' data-testid='basic-details-page'>
      <header className='basic-details-page__banner'>
        <Link to='/home' className='basic-details-page__logo-link' aria-label='Go to home'>
          <CBLogoV2 width='140' height='33' />
        </Link>
      </header>

      <main className='basic-details-page__form-section' aria-label='Basic details'>
        <div className='basic-details-page__form-card'>
          <header className='basic-details-page__intro'>
            <h1 className='basic-details-page__intro-title'>Let us know you better!</h1>
          </header>
          {isLoading ? (
            <div className='basic-details-page__loading' role='status' aria-live='polite'>
              <div className='basic-details-page__loading-spinner' aria-hidden='true' />
              <span>Loading your details…</span>
            </div>
          ) : (
            <BasicDetailsPresenter
              initialValues={initialValues}
              validationSchema={basicDetailsSchema}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </main>
    </div>
  )
}

export { BasicDetailsContainer }
